// @ts-nocheck
import React, { useState } from 'react'
import { Card, Row, Col, Statistic, Typography, List, Tag, Spin, Alert, Progress, Empty, Button, message } from 'antd'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gmailService } from '../services/gmailService'
import MaterialIcon from '../components/MaterialIcon'
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

const { Title, Text } = Typography

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96']

const Dashboard: React.FC = () => {
  const queryClient = useQueryClient()
  
  const { data: analytics, isLoading, error } = useQuery({
    queryKey: ['gmail-analytics'],
    queryFn: async () => {
      const response = await gmailService.getAnalytics()
      return response.data
    },
    refetchInterval: 60000, // Refetch every minute
  })

  // Mutation for re-linking customers
  const relinkMutation = useMutation({
    mutationFn: () => gmailService.relinkCustomers(),
    onSuccess: (response) => {
      message.success(`Successfully linked ${response.data.linkedCount} email threads to customers!`)
      // Refetch analytics to update the customer link rate
      queryClient.invalidateQueries({ queryKey: ['gmail-analytics'] })
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to re-link customers')
    },
  })

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading analytics...</div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Analytics"
        description="Failed to load email analytics. Please try again."
        type="error"
        showIcon
      />
    )
  }

  if (!analytics) {
    return null
  }

  // Prepare AI classifications data for pie chart
  const aiClassData = Object.entries(analytics.aiClassifications || {}).map(([name, value]) => ({
    name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value
  }))

  // Calculate percentage metrics
  const readRate = analytics.totalEmails > 0 
    ? Math.round(((analytics.totalEmails - analytics.unread) / analytics.totalEmails) * 100) 
    : 0

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          <MaterialIcon icon="analytics" style={{ marginRight: '12px', fontSize: '32px', color: '#1890ff' }} />
          Email Analytics
        </Title>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {analytics.lastSyncAt && (
            <Text type="secondary">
              Last synced: {new Date(analytics.lastSyncAt).toLocaleString()}
            </Text>
          )}
          <Button
            type="primary"
            icon={<MaterialIcon icon="link" style={{ fontSize: '18px' }} />}
            loading={relinkMutation.isPending}
            onClick={() => relinkMutation.mutate()}
          >
            Re-Link Customers
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Emails"
              value={analytics.totalEmails}
              prefix={<MaterialIcon icon="mail" style={{ color: '#3f8600' }} />}
              valueStyle={{ color: '#3f8600' }}
            />
            <Progress 
              percent={100} 
              showInfo={false} 
              strokeColor="#3f8600"
              size="small"
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Received"
              value={analytics.received}
              prefix={<MaterialIcon icon="inbox" style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
              suffix={<Text type="secondary" style={{ fontSize: '14px' }}>/ {analytics.sent} sent</Text>}
            />
            <Progress 
              percent={analytics.totalEmails > 0 ? (analytics.received / analytics.totalEmails) * 100 : 0} 
              showInfo={false} 
              strokeColor="#1890ff"
              size="small"
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Unread Emails"
              value={analytics.unread}
              prefix={<MaterialIcon icon="mark_email_unread" style={{ color: '#faad14' }} />}
              valueStyle={{ color: analytics.unread > 0 ? '#faad14' : '#52c41a' }}
              suffix={<Text type="secondary" style={{ fontSize: '14px' }}>({readRate}% read)</Text>}
            />
            <Progress 
              percent={readRate} 
              showInfo={false} 
              strokeColor="#52c41a"
              size="small"
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Linked Customers"
              value={analytics.linkedCustomers}
              prefix={<MaterialIcon icon="person" style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
              suffix={<Text type="secondary" style={{ fontSize: '14px' }}>({analytics.linkedThreads}/{analytics.totalEmails} threads)</Text>}
            />
            <Progress 
              percent={analytics.threadLinkRate || 0} 
              showInfo={true}
              format={() => `${analytics.threadLinkRate}% linked`}
              strokeColor="#722ed1"
              size="small"
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Additional Metrics */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Linked Jobs"
              value={analytics.linkedJobs}
              prefix={<MaterialIcon icon="work" style={{ color: '#13c2c2' }} />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Response Time"
              value={analytics.avgResponseTime}
              suffix="h"
              prefix={<MaterialIcon icon="schedule" style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Response Rate"
              value={analytics.responseRate}
              suffix="%"
              prefix={<MaterialIcon icon="trending_up" style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Sent Emails"
              value={analytics.sent}
              prefix={<MaterialIcon icon="send" style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Open Rate"
              value={analytics.tracking?.openRate || 0}
              suffix="%"
              prefix={
                <span className="material-icons" style={{ fontSize: '20px', color: '#52c41a', marginRight: '8px' }}>
                  drafts
                </span>
              }
              valueStyle={{ color: '#52c41a' }}
            />
            <Progress 
              percent={analytics.tracking?.openRate || 0} 
              showInfo={false} 
              strokeColor="#52c41a"
              size="small"
              style={{ marginTop: '8px' }}
            />
            {analytics.tracking?.totalSent !== undefined && analytics.sent !== undefined && (
              <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {analytics.tracking.totalSent} of {analytics.sent} emails tracked
              </Text>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Click Rate"
              value={analytics.tracking?.clickRate || 0}
              suffix="%"
              prefix={
                <span className="material-icons" style={{ fontSize: '20px', color: '#1890ff', marginRight: '8px' }}>
                  touch_app
                </span>
              }
              valueStyle={{ color: '#1890ff' }}
            />
            <Progress 
              percent={analytics.tracking?.clickRate || 0} 
              showInfo={false} 
              strokeColor="#1890ff"
              size="small"
              style={{ marginTop: '8px' }}
            />
            {analytics.tracking?.totalSent !== undefined && analytics.sent !== undefined && (
              <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {analytics.tracking.totalSent} of {analytics.sent} emails tracked
              </Text>
            )}
          </Card>
        </Col>
      </Row>

      {/* Charts Section */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        {/* Emails by Day */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <span>
                <MaterialIcon icon="calendar_today" style={{ marginRight: '8px' }} />
                Emails This Week
              </span>
            }
          >
            {analytics.emailsByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.emailsByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1890ff" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No data for the past week" />
            )}
          </Card>
        </Col>

        {/* Emails by Hour */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <span>
                <MaterialIcon icon="schedule" style={{ marginRight: '8px' }} />
                Email Activity by Hour
              </span>
            }
          >
            {analytics.emailsByHour.some((h: any) => h.count > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analytics.emailsByHour}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" label={{ value: 'Hour of Day', position: 'insideBottom', offset: -5 }} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#52c41a" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No recent activity" />
            )}
          </Card>
        </Col>

        {/* AI Classifications */}
        {aiClassData.length > 0 && (
          <Col xs={24} lg={12}>
            <Card 
              title={
                <span>
                  <MaterialIcon icon="smart_toy" style={{ marginRight: '8px' }} />
                  AI Email Classifications
                </span>
              }
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={aiClassData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {aiClassData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        )}

        {/* Top Senders */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <span>
                <MaterialIcon icon="leaderboard" style={{ marginRight: '8px' }} />
                Top Email Senders
              </span>
            }
          >
            {analytics.topSenders.length > 0 ? (
              <List
                dataSource={analytics.topSenders}
                renderItem={(sender: any, index: number) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: COLORS[index % COLORS.length],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {index + 1}
                        </div>
                      }
                      title={sender.name}
                      description={sender.email}
                    />
                    <Tag color={COLORS[index % COLORS.length]}>{sender.count} emails</Tag>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No sender data" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Quick Stats */}
      <Card 
        style={{ marginTop: '24px' }}
        title={
          <span>
            <MaterialIcon icon="insights" style={{ marginRight: '8px' }} />
            Quick Insights
          </span>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <MaterialIcon icon="mail" style={{ fontSize: '48px', color: '#1890ff' }} />
              <Title level={3} style={{ marginTop: '16px', marginBottom: '8px' }}>
                {analytics.totalEmails > 0 ? Math.round(analytics.received / (analytics.totalEmails / 100)) : 0}%
              </Title>
              <Text type="secondary">Incoming Email Ratio</Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <MaterialIcon icon="person" style={{ fontSize: '48px', color: '#722ed1' }} />
              <Title level={3} style={{ marginTop: '16px', marginBottom: '8px' }}>
                {analytics.linkedCustomers}/{analytics.totalEmails}
              </Title>
              <Text type="secondary">Customer Link Rate</Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <MaterialIcon icon="trending_up" style={{ fontSize: '48px', color: '#52c41a' }} />
              <Title level={3} style={{ marginTop: '16px', marginBottom: '8px' }}>
                {readRate}%
              </Title>
              <Text type="secondary">Email Read Rate</Text>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default Dashboard
